import styled from '@emotion/styled';
import { Stack, Text, Icon, Card, tokens } from '@hotjar/hotsauce';
import { useTranslation } from '@hotjar/translations';
import type { QuestionTypes } from '../../../../../../domain/Surveys/Questions';
import { getQuestionLabelAndIcon } from '../../../../utils';

export interface QuestionBankQuestionProps {
  type: QuestionTypes;
  text: string;
}

export const DragHandleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-items: center;
  flex-shrink: 0;
  flex-grow: 0;
  color: ${tokens.color.neutral_300};
`;

export const QuestionBankCard = styled(Card)`
  margin: 0 ${tokens.spacer.x3};
  :hover {
    ${tokens.transition('box-shadow')};
    ${tokens.elevation(3)};

    ${DragHandleContainer} {
      ${tokens.transition('color')};
      color: ${tokens.color.neutral_400};
    }
  }
`;

export const DragHandle = (): JSX.Element => {
  return (
    <DragHandleContainer>
      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="16">
        <g fill="currentColor">
          <circle cx="2" cy="2" r="2" />
          <circle cx="8" cy="2" r="2" />
          <circle cx="2" cy="8" r="2" />
          <circle cx="8" cy="8" r="2" />
          <circle cx="2" cy="14" r="2" />
          <circle cx="8" cy="14" r="2" />
        </g>
      </svg>
    </DragHandleContainer>
  );
};

const QuestionContainer = styled(Stack)`
  align-items: center;
`;

export const QuestionBankQuestion = ({ type, text }: QuestionBankQuestionProps): JSX.Element => {
  const { label, icon } = getQuestionLabelAndIcon(type);
  const { t } = useTranslation('feedback', {
    keyPrefix: 'wizard.section.questions.question-type.label',
  });
  return (
    <QuestionBankCard>
      <Card.Body>
        <QuestionContainer direction="row">
          <DragHandle />
          <Stack direction="column">
            <Stack direction="row" spacing="x0">
              <Icon icon={icon} color="text_light" />
              <Text textColor="text_light" textType="caption">
                {t(label)}
              </Text>
            </Stack>
            <Text textColor="text_default" textType="body2">
              {text}
            </Text>
          </Stack>
        </QuestionContainer>
      </Card.Body>
    </QuestionBankCard>
  );
};
